import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { MODEL_VERSION } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { FormParams } from '@/app/stores/slices/task-form';
import { validateRecaptionPrompt } from '@/app/utils';
import type { FormTabType } from '../types';

interface UseFormValidationOptions {
  taskType: Exclude<FormTabType, 'scene'>;
  formParams: FormParams;
  onOpenPlanDialog?: (fromPage?: string) => void;
}

export const useFormValidation = ({ taskType, formParams, onOpenPlanDialog }: UseFormValidationOptions) => {
  const t = useTranslations();

  const { modelVersion, userInfo, remainingTrialsQ1 } = useStore((state) => ({
    modelVersion: state[`${taskType}FormParams`].modelVersion,
    userInfo: state.userInfo,
    remainingTrialsQ1: state.trialsQ1?.remaining,
  }));

  const isFree = userInfo?.subs_plan === 'free';

  // 需要升级才能使用当前模型（按钮可点，点击时弹升级弹窗）
  const shouldUpgradeForModel = useMemo(() => {
    if ((modelVersion === MODEL_VERSION.Q3_A || modelVersion === MODEL_VERSION.Q3_HH) && isFree) return true;
    if (
      taskType !== 'reference2image' &&
      modelVersion === MODEL_VERSION.Q1 &&
      isFree &&
      typeof remainingTrialsQ1 === 'number' &&
      remainingTrialsQ1 <= 0
    )
      return true;
    return false;
  }, [modelVersion, isFree, taskType, remainingTrialsQ1]);

  const errorMessage = useMemo(() => {
    const isProMode = formParams.isProMode;

    // 专业模式需要检查所有必填 key
    if (isProMode && !validateRecaptionPrompt(formParams.recaptionPrompt)) {
      return t('create.createForm.error.recaptionRequiredFields');
    }

    return '';
  }, [formParams, t]);

  const handleUpgradeClick = () => onOpenPlanDialog?.('create_button');

  return { errorMessage, shouldUpgradeForModel, handleUpgradeClick };
};
